// AI Smart Search. Turns a natural-language query into a strict SmartSearchFilters
// object using Lovable AI. Public (unauthenticated); rate-limited by client hash.

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import { createHash } from "crypto";
import type { Database } from "@/integrations/supabase/types";
import { normaliseSmartSearch, type SmartSearchFilters } from "@/lib/smart-search";

const Input = z.object({
  query: z.string().min(1).max(200),
  client_hint: z.string().max(200).optional(),
});

const SYSTEM = `You convert natural-language trading-card search queries into a strict JSON filter.
Return ONLY compact JSON with these keys (all optional, null when unknown):
{ "grader": "PSA|BGS|CGC|ACE|PG|SGC|TAG|AGS|ISA|OTHER|RAW|null",
  "grade": "string|null (Authentic, Ungraded, or a numeric like 9, 9.5, 10)",
  "card_type": "raw|graded|sealed|null",
  "game": "string|null (pokemon/one_piece/yugioh/dbs/mtg or lowercase franchise)",
  "character": "string|null",
  "set": "string|null",
  "language": "string|null",
  "min_price": number|null (in £),
  "max_price": number|null (in £),
  "keywords": ["array","of","lowercase","tokens"] }
Do not include any other keys. Do not add commentary.`;

function isNewApiKey(v: string): boolean {
  return v.startsWith("sb_publishable_") || v.startsWith("sb_secret_");
}

function shim(key: string): typeof fetch {
  return (input, init) => {
    const h = new Headers(init?.headers);
    if (isNewApiKey(key) && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
    h.set("apikey", key);
    return fetch(input, { ...init, headers: h });
  };
}

export const parseSmartSearch = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => Input.parse(d))
  .handler(async ({ data }): Promise<{ filters: SmartSearchFilters; source: "ai" | "fallback" }> => {
    // Rate-limit by IP hash — read x-forwarded-for from request if available.
    let clientHash = "anonymous";
    try {
      const { getRequest } = await import("@tanstack/react-start/server");
      const req = getRequest();
      const ip = (req.headers.get("x-forwarded-for") ?? "").split(",")[0]?.trim() || "unknown";
      clientHash = createHash("sha256").update(`${ip}:${data.client_hint ?? ""}`).digest("hex").slice(0, 32);
    } catch {
      // ignore — dev-time
    }

    // Shared limiter: anonymous callers get the tight AI budget, signed-in
    // callers a larger one. Over-limit degrades to the keyword fallback
    // rather than erroring, so search keeps working.
    try {
      const { enforceRateLimit } = await import("@/lib/rate-limit.server");
      await enforceRateLimit("smart-search", { anonPreset: "aiAnon", userPreset: "aiUser" });
    } catch {
      return { filters: fallbackFromKeywords(data.query), source: "fallback" };
    }


    const SUPABASE_URL = process.env.SUPABASE_URL;
    const SUPABASE_KEY = process.env.SUPABASE_PUBLISHABLE_KEY;
    if (SUPABASE_URL && SUPABASE_KEY) {
      const anon = createClient<Database>(SUPABASE_URL, SUPABASE_KEY, {
        auth: { persistSession: false, autoRefreshToken: false, storage: undefined },
        global: { fetch: shim(SUPABASE_KEY) },
      });
      const { data: allowed } = await anon.rpc("can_smart_search", { _client_hash: clientHash });
      if (allowed === false) {
        return { filters: fallbackFromKeywords(data.query), source: "fallback" };
      }
    }

    const apiKey = process.env.LOVABLE_API_KEY;
    if (!apiKey) return { filters: fallbackFromKeywords(data.query), source: "fallback" };

    try {
      const res = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Lovable-API-Key": apiKey },
        body: JSON.stringify({
          model: "google/gemini-3.1-flash-lite",
          messages: [
            { role: "system", content: SYSTEM },
            { role: "user", content: data.query },
          ],
          response_format: { type: "json_object" },
        }),
      });
      if (!res.ok) return { filters: fallbackFromKeywords(data.query), source: "fallback" };
      const payload = (await res.json()) as { choices?: Array<{ message?: { content?: string } }> };
      const raw = payload.choices?.[0]?.message?.content ?? "{}";
      let json: unknown = {};
      try { json = JSON.parse(raw); } catch { json = {}; }
      return { filters: normaliseSmartSearch(json), source: "ai" };
    } catch {
      return { filters: fallbackFromKeywords(data.query), source: "fallback" };
    }
  });

function fallbackFromKeywords(query: string): SmartSearchFilters {
  return normaliseSmartSearch({
    keywords: query
      .toLowerCase()
      .split(/\s+/)
      .filter((s) => s.length > 1),
  });
}
